import React, { useState, useEffect, useRef } from 'react';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { FileText, Download, Search, Loader2, Users, Truck, Calendar as CalendarIcon, Filter, UserX, FileDown, AlertTriangle, Mail, X, Check, LayoutList } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import jsPDF from 'jspdf';
import { safeHtml2Canvas } from '../utils/safeHtml2Canvas';
import * as XLSX from 'xlsx';
import { getCachedStudents, getCachedStaff, getCachedClasses, getCachedQuery } from '../services/dataCacheService';
import { SchoolClass } from '../types';
import { getHolidays, getHolidayForDate } from '../services/holidayService';
import { useAcademicYear } from '../context/AcademicYearContext';
import { isSameClass } from '../services/academicYearService';
import { SCHOOL_LOGO_BASE64 } from '../utils/logoBase64';

interface ReportsProps {
  initialReportType?: 'attendance' | 'transport' | 'staff' | 'general';
  userRole?: string | null;
  assignedClassId?: string | null;
}

const Reports: React.FC<ReportsProps> = ({ initialReportType, userRole, assignedClassId }) => {
  const { selectedYear: academicYearId, currentYearObj } = useAcademicYear();
  const [reportType, setReportType] = useState<'attendance' | 'transport' | 'staff' | 'general' | 'individual'>(initialReportType || 'attendance');
  const [dateRangeType, setDateRangeType] = useState<'daily' | 'month' | 'custom' | 'all'>('daily');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isWeekend, setIsWeekend] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('all');
  const [selectedCarrierId, setSelectedCarrierId] = useState('all');
  const [selectedStudentId, setSelectedStudentId] = useState('all');
  const [selectedStaffId, setSelectedStaffId] = useState('all');
  const [staffReportType, setStaffReportType] = useState<'all' | 'educational' | 'assistant'>('all');
  const [staffDisplayMode, setStaffDisplayMode] = useState<'detailed' | 'summary'>('detailed');
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [searched, setSearched] = useState(false);
  const [exportFilter, setExportFilter] = useState<'all' | 'present'>('all');
  const reportRef = useRef<HTMLDivElement>(null);

  const getMonthName = (dateStr?: string) => {
    const months = [
      'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
      'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];
    if (dateRangeType === 'month' && selectedMonth >= 1 && selectedMonth <= 12) {
      return months[selectedMonth - 1];
    }
    const target = dateStr || (dateRangeType === 'daily' ? date : startDate);
    if (target) {
      const parts = target.trim().split('-');
      if (parts.length >= 2) {
        const m = parseInt(parts[1], 10);
        if (m >= 1 && m <= 12) return months[m - 1];
      }
      const d = new Date(target.includes('T') ? target : `${target}T12:00:00`);
      if (!isNaN(d.getTime())) {
        return months[d.getMonth()];
      }
    }
    if (selectedMonth >= 1 && selectedMonth <= 12) {
      return months[selectedMonth - 1];
    }
    return months[new Date().getMonth()];
  };

  const getReportYear = (dateStr?: string) => {
    if (dateRangeType === 'month' && selectedYear) return selectedYear;
    const target = dateStr || (dateRangeType === 'daily' ? date : startDate);
    if (target) {
      const parts = target.trim().split('-');
      if (parts.length >= 1 && parts[0].length === 4) {
        const y = parseInt(parts[0], 10);
        if (!isNaN(y) && y > 2000) return y;
      }
    }
    return selectedYear || new Date().getFullYear();
  };

  // Individual Report States
  const [individualType, setIndividualType] = useState<'student' | 'staff'>('student');
  const [people, setPeople] = useState<any[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState('');

  useEffect(() => {
    fetchClasses();
    fetchCarriers();
    checkWeekend();
  }, [date]);

  const fetchCarriers = async () => {
    try {
      // In this app, Carriers are stored in 'transportLines' collection
      const results = await getCachedQuery('transport_lines', query(collection(db, 'transportLines'), orderBy('name')));
      const sorted = results.map((t: any) => ({
        id: t.id,
        name: t.name || t.id
      })).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'he'));
      setCarriers(sorted);
      return sorted;
    } catch (error) {
      console.error("Error fetching carriers:", error);
      return [];
    }
  };

  useEffect(() => {
    if (dateRangeType === 'month') {
      const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
      const lastDay = new Date(selectedYear, selectedMonth, 0);
      
      const format = (d: Date) => {
        return d.getFullYear() + '-' + 
               String(d.getMonth() + 1).padStart(2, '0') + '-' + 
               String(d.getDate()).padStart(2, '0');
      };
      
      setStartDate(format(firstDay));
      setEndDate(format(lastDay));
    }
  }, [dateRangeType, selectedMonth, selectedYear]);

  const checkWeekend = () => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday
    setIsWeekend(day === 5 || day === 6);
  };

  useEffect(() => {
    if (reportType === 'individual' || reportType === 'attendance' || reportType === 'staff') {
      fetchPeople();
    }
  }, [reportType, individualType, selectedClassId]);

  const fetchPeople = async () => {
    try {
      if ((reportType === 'individual' && individualType === 'student') || reportType === 'attendance') {
        const allStudents = await getCachedStudents(false, academicYearId);
        let filtered = allStudents;
        if (selectedClassId !== 'all') {
          const targetClass = classes.find(c => c.id === selectedClassId || c.name === selectedClassId);
          filtered = filtered.filter(s => 
            s.classId === selectedClassId || 
            s.className === selectedClassId ||
            isSameClass(
              { id: s.classId, name: s.className },
              { id: targetClass?.id || selectedClassId, name: targetClass?.name || selectedClassId }
            )
          );
        }
        const mapped = filtered.map(d => ({ id: d.id, name: d.fullName }));
        setPeople(mapped);
        return mapped;
      } else {
        // This handles reportType === 'staff' OR (reportType === 'individual' && individualType === 'staff')
        const allStaff = await getCachedStaff();
        const mapped = allStaff.map(d => ({ id: d.id, name: d.fullName, staffType: d.staffType }));
        setPeople(mapped);
        return mapped;
      }
    } catch (error) {
      console.error("Error fetching people:", error);
      return [];
    }
  };

  useEffect(() => {
    if (userRole === 'teacher' && assignedClassId) {
      setSelectedClassId(assignedClassId);
    }
  }, [userRole, assignedClassId]);

  // Automatically fetch report on mount and whenever key parameters change
  useEffect(() => {
    setData([]);
    setLoading(true);
    const timer = setTimeout(() => {
      fetchReport();
    }, 250);
    return () => clearTimeout(timer);
  }, [
    reportType,
    dateRangeType,
    date,
    startDate,
    endDate,
    selectedMonth,
    selectedYear,
    selectedCarrierId,
    selectedClassId,
    selectedStudentId,
    selectedStaffId,
    staffReportType,
    staffDisplayMode,
    individualType,
    selectedPersonId
  ]);

  const fetchClasses = async () => {
    try {
      const classList = await getCachedClasses();
      const sorted = classList.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he', { numeric: true }));
      setClasses(sorted);
      return sorted;
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'classes');
      console.error("Error fetching classes:", error);
      return [];
    }
  };

  const calculateDaysInRange = (start: any, end: any, rangeStart: string, rangeEnd: string) => {
    if (!start || !end || typeof start !== 'string' || typeof end !== 'string') return 0;
    try {
      const s = new Date(start);
      const e = new Date(end);
      const rs = new Date(rangeStart);
      const re = new Date(rangeEnd);
      
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || isNaN(rs.getTime()) || isNaN(re.getTime())) return 0;

      // The actual start is the max of absence start and range start
      const actualStart = s > rs ? s : rs;
      // The actual end is the min of absence end and range end
      const actualEnd = e < re ? e : re;
      
      if (actualStart > actualEnd) return 0;
      
      const diffTime = Math.abs(actualEnd.getTime() - actualStart.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } catch (e) {
      return 0;
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setSearched(true);
    setData([]);
    
    const effectiveStartDate = dateRangeType === 'daily' ? date : startDate;
    const effectiveEndDate = dateRangeType === 'daily' ? date : endDate;

    console.log(`Fetching report: ${reportType}, Range: ${effectiveStartDate} to ${effectiveEndDate}`);
    
    // Fetch latest data to ensure name changes in settings are reflected
    const [currentCarriers, currentClasses, currentPeople, holidays] = await Promise.all([
      fetchCarriers(),
      fetchClasses(),
      (reportType === 'individual' || reportType === 'attendance' || reportType === 'transport') ? fetchPeople() : Promise.resolve([]),
      getHolidays().catch(err => { console.error("Error fetching holidays in reports:", err); return []; })
    ]);

    const todayStr = new Date().toISOString().split('T')[0];

    const normalizeDateStr = (d: any): string => {
      if (!d) return '';
      if (typeof d === 'string') {
        const s = d.trim().split('T')[0];
        const parts = s.split(/[-/]/);
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
          } else if (parts[2].length === 4) {
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          }
        }
        return s;
      }
      return '';
    };

    try {
      const workdayDates: string[] = [];
      let currD = new Date(effectiveStartDate + 'T12:00:00');
      const lastD = new Date(effectiveEndDate + 'T12:00:00');
      while (currD <= lastD) {
        const dayNum = currD.getDay();
        const y = currD.getFullYear();
        const m = String(currD.getMonth() + 1).padStart(2, '0');
        const d = String(currD.getDate()).padStart(2, '0');
        const dateStr = `${y}-${m}-${d}`;
        // If daily, always include the selected date (even if weekend).
        // If range, exclude Friday and Saturday as school transport is inactive.
        if (dateRangeType === 'daily' || (dayNum !== 5 && dayNum !== 6)) {
          workdayDates.push(dateStr);
        }
        currD.setDate(currD.getDate() + 1);
      }

      if (reportType === 'attendance') {
        const q = query(
          collection(db, 'studentAttendance'),
          where('date', '>=', effectiveStartDate),
          where('date', '<=', effectiveEndDate),
          orderBy('date', 'asc')
        );
        const results = await getCachedQuery(`attendance_${effectiveStartDate}_${effectiveEndDate}`, q, true);
        
        const allStudents = await getCachedStudents(false, academicYearId);
        const targetClass = classes.find(c => c.id === selectedClassId || c.name === selectedClassId);
        let studentsInScope = allStudents.filter(s => {
          if (s.active === false) return false;
          if (selectedStudentId !== 'all') {
            return String(s.id).trim() === String(selectedStudentId).trim();
          }
          if (selectedClassId !== 'all') {
            const matches = s.classId === selectedClassId || s.className === selectedClassId ||
              isSameClass(
                { id: s.classId, name: s.className },
                { id: targetClass?.id || selectedClassId, name: targetClass?.name || selectedClassId }
              );
            if (!matches) return false;
          }
          return true;
        });

        // Fallback: If a specific student was selected, make sure they are in scope
        if (selectedStudentId !== 'all' && studentsInScope.length === 0) {
          const found = currentPeople.find(p => p.id === selectedStudentId);
          if (found) {
            studentsInScope = [{
              id: found.id,
              fullName: found.name,
              classId: selectedClassId !== 'all' ? selectedClassId : '',
              className: targetClass?.name || ''
            }];
          }
        }

        // Use a map for quick lookup
        const attendanceMap = new Map();
        results.forEach((att: any) => {
          const sId = String(att.studentId || '').trim();
          const dStr = normalizeDateStr(att.date);
          if (sId && dStr) {
            attendanceMap.set(`${sId}_${dStr}`, att);
          }
          if (att.studentName && dStr) {
            attendanceMap.set(`${att.studentName.trim()}_${dStr}`, att);
          }
          if (att.id && typeof att.id === 'string' && dStr) {
            const parts = att.id.split('_');
            const lastPart = parts[parts.length - 1];
            if (lastPart) {
              attendanceMap.set(`${lastPart}_${dStr}`, att);
            }
          }
        });

        const finalResults = [];
        for (const dateStr of workdayDates) {
          const holiday = getHolidayForDate(dateStr, holidays);
          const isFutureDate = dateStr > todayStr;

          for (const student of studentsInScope) {
            const sId = String(student.id || '').trim();
            const sName = student.fullName?.trim() || '';
            let att = attendanceMap.get(`${sId}_${dateStr}`);
            if (!att && sName) {
              att = attendanceMap.get(`${sName}_${dateStr}`);
            }
            if (!att && student.nationalId) {
              att = attendanceMap.get(`${student.nationalId.trim()}_${dateStr}`);
            }

            if (holiday) {
              finalResults.push({
                studentId: sId,
                studentName: student.fullName || att?.studentName,
                classId: student.classId || att?.classId,
                className: student.className || currentClasses.find(c => c.id === student.classId)?.name || att?.className || '',
                date: dateStr,
                present: false,
                notes: `חופשה: ${holiday.name}`,
                isPlaceholder: false,
                isHolidayDay: true,
                holidayName: holiday.name
              });
            } else if (att) {
              const isPresent = att.present === true || att.present === 'true' || att.status === 'present';
              finalResults.push({
                ...att,
                studentId: sId,
                studentName: student.fullName || att.studentName,
                classId: student.classId || att.classId,
                className: student.className || currentClasses.find(c => c.id === student.classId)?.name || att.className || '',
                date: dateStr,
                present: isPresent,
                notes: att.notes || '',
                isPlaceholder: false,
                isHolidayDay: false,
                holidayName: ''
              });
            } else if (!isFutureDate) {
              // Past or current school day: in this system, students are present by default unless recorded absent
              finalResults.push({
                studentId: sId,
                studentName: student.fullName,
                classId: student.classId,
                className: student.className || currentClasses.find(c => c.id === student.classId)?.name || '',
                date: dateStr,
                present: true,
                notes: '',
                isPlaceholder: false,
                isHolidayDay: false,
                holidayName: ''
              });
            } else {
              // Future date that has not yet occurred
              finalResults.push({
                studentId: student.id,
                studentName: student.fullName,
                classId: student.classId,
                className: currentClasses.find(c => c.id === student.classId)?.name || '',
                date: dateStr,
                present: false,
                notes: '(טרם דווח)',
                isPlaceholder: true,
                isHolidayDay: false,
                holidayName: ''
              });
            }
          }
        }

        setData(finalResults.sort((a, b) => {
          const dateComp = a.date.localeCompare(b.date);
          if (dateComp !== 0) return dateComp;
          return (a.studentName || '').localeCompare(b.studentName || '');
        }));
      } else if (reportType === 'general') {
        if (dateRangeType === 'daily') {
          // Fetch all students for current academic year
          const allStudents = await getCachedStudents(false, academicYearId);
          
          // Fetch attendance for the date
          const attendanceQuery = query(
            collection(db, 'studentAttendance'),
            where('date', '==', date)
          );
          const attendanceResults = await getCachedQuery(`attendance_${date}`, attendanceQuery);
          const attendanceMap: Record<string, any> = {};
          attendanceResults.forEach((att: any) => {
            attendanceMap[att.studentId] = att;
          });

          // Group by class
          const classStats: Record<string, { className: string, total: number, present: number, absentNames: string[] }> = {};
          
          // Initialize with all classes
          currentClasses.forEach(cls => {
            classStats[cls.id] = { className: cls.name, total: 0, present: 0, absentNames: [] };
          });

          allStudents.forEach((student: any) => {
            if (!classStats[student.classId]) return;
            
            classStats[student.classId].total++;
            const att = attendanceMap[student.id];
            if (att && (att.present === false || att.status === 'absent')) {
              classStats[student.classId].absentNames.push(student.fullName);
            } else {
              classStats[student.classId].present++;
            }
          });

          setData(Object.values(classStats).sort((a, b) => (a.className || '').localeCompare(b.className || '', 'he', { numeric: true })));
        } else {
          // For range in general report, aggregate
          const allStudents = await getCachedStudents(false, academicYearId);
          
          const attendanceQuery = query(
            collection(db, 'studentAttendance'),
            where('date', '>=', effectiveStartDate),
            where('date', '<=', effectiveEndDate)
          );
          const attendanceResults = (await getCachedQuery(`attendance_${effectiveStartDate}_${effectiveEndDate}`, attendanceQuery))
            .filter((att: any) => {
              const d = new Date(att.date + 'T12:00:00');
              const day = d.getDay();
              return day !== 5 && day !== 6;
            });
          
          const classStats: Record<string, { className: string, total: number, present: number, absentNames: string[] }> = {};
          currentClasses.forEach(cls => {
            classStats[cls.id] = { className: cls.name, total: 0, present: 0, absentNames: [] };
          });

          attendanceResults.forEach((att: any) => {
            if (classStats[att.classId]) {
              if (att.present) classStats[att.classId].present++;
              else classStats[att.classId].absentNames.push(`${att.studentName} (${att.date})`);
            }
          });
          
          const uniqueDates = new Set(attendanceResults.map((d: any) => d.date)).size || 1;
          allStudents.forEach((s: any) => {
            if (classStats[s.classId]) classStats[s.classId].total += uniqueDates;
          });

          setData(Object.values(classStats).sort((a, b) => (a.className || '').localeCompare(b.className || '', 'he', { numeric: true })));
        }
      } else if (reportType === 'transport') {
        let transportResults: any[] = [];
        try {
          const q = query(
            collection(db, 'transportAttendance'),
            where('date', '>=', effectiveStartDate),
            where('date', '<=', effectiveEndDate)
          );
          transportResults = await getCachedQuery(`transport_${effectiveStartDate}_${effectiveEndDate}`, q, true);
        } catch (queryErr) {
          console.warn("Transport range query warning:", queryErr);
        }

        // Fallback: If query returned 0 results or failed, also check direct documents for each date
        if (transportResults.length === 0 && workdayDates.length <= 31) {
          try {
            const docPromises = workdayDates.map(async (dStr) => {
              try {
                const snap = await getDoc(doc(db, 'transportAttendance', `transport_${dStr}`));
                if (snap.exists()) {
                  return { id: snap.id, ...snap.data() };
                }
              } catch (e) {}
              return null;
            });
            const fetched = await Promise.all(docPromises);
            const validDocs = fetched.filter(Boolean);
            if (validDocs.length > 0) {
              transportResults = validDocs;
            }
          } catch (e) {
            console.error("Fallback doc fetch error:", e);
          }
        }
        
        // Multi-key map for instant, resilient matching
        const transportMap = new Map<string, any>();
        transportResults.forEach((dayData: any) => {
          const dayDate = dayData.date;
          const transporters = dayData.transporters || [];
          transporters.forEach((t: any) => {
            if (t.id) transportMap.set(`${t.id}_${dayDate}`, t);
            if (t.name) {
              transportMap.set(`${t.name}_${dayDate}`, t);
              transportMap.set(`${t.name.trim()}_${dayDate}`, t);
              transportMap.set(`${t.name.trim().toLowerCase()}_${dayDate}`, t);
            }
          });
        });

        // Collect all known carriers from transportLines + all transporters found in attendance docs
        const allKnownCarriersMap = new Map<string, { id: string; name: string }>();
        currentCarriers.forEach(c => {
          if (c && (c.name || c.id)) {
            const key = c.id || c.name;
            allKnownCarriersMap.set(key, { id: c.id, name: c.name || c.id });
          }
        });

        transportResults.forEach((dayData: any) => {
          const transporters = dayData.transporters || [];
          transporters.forEach((t: any) => {
            if (t && (t.name || t.id)) {
              const key = t.id || t.name;
              if (!allKnownCarriersMap.has(key)) {
                allKnownCarriersMap.set(key, { id: t.id || t.name, name: t.name || t.id });
              }
            }
          });
        });

        const allKnownCarriers = Array.from(allKnownCarriersMap.values()).sort((a, b) => 
          (a.name || '').localeCompare(b.name || '', 'he')
        );

        // Keep dropdown in sync if new carriers discovered
        if (allKnownCarriers.length > carriers.length) {
          setCarriers(allKnownCarriers);
        }
        
        let carriersInScope = allKnownCarriers;
        if (selectedCarrierId !== 'all') {
          carriersInScope = allKnownCarriers.filter(c => 
            c.id === selectedCarrierId || 
            c.name === selectedCarrierId ||
            (c.name && selectedCarrierId && c.name.trim().toLowerCase() === selectedCarrierId.trim().toLowerCase())
          );
          if (carriersInScope.length === 0) {
            const found = currentCarriers.find(c => c.id === selectedCarrierId || c.name === selectedCarrierId);
            carriersInScope = found ? [found] : [{ id: selectedCarrierId, name: selectedCarrierId }];
          }
        }

        const finalResults = [];
        for (const dateStr of workdayDates) {
          const holiday = getHolidayForDate(dateStr, holidays);
          const d = new Date(dateStr + 'T12:00:00');
          const isWeekendDay = d.getDay() === 5 || d.getDay() === 6;

          for (const carrier of carriersInScope) {
            const att = transportMap.get(`${carrier.id}_${dateStr}`) || 
                        transportMap.get(`${carrier.name}_${dateStr}`) ||
                        (carrier.name ? transportMap.get(`${carrier.name.trim()}_${dateStr}`) : undefined) ||
                        (carrier.name ? transportMap.get(`${carrier.name.trim().toLowerCase()}_${dateStr}`) : undefined);
            
            if (att) {
              const isHolidayDay = !!holiday || att.isHoliday || (att.notes && (att.notes.includes('חופשה') || att.notes.includes('לא פעיל')));
              finalResults.push({ 
                ...att, 
                date: dateStr, 
                id: carrier.id, 
                name: carrier.name,
                arrived: att.arrived === true,
                notes: att.notes || (holiday ? `חופשה: ${holiday.name}` : (isWeekendDay ? 'שישי/שבת - לא פעיל' : '')),
                isHolidayDay: !!isHolidayDay,
                holidayName: holiday ? holiday.name : (att.notes && att.notes.includes('חופשה') ? att.notes : ''),
                isPlaceholder: false
              });
            } else {
              // Exact default logic matching TransportAttendance screen:
              // Weekdays default to arrived: true (נוכח)
              // Holidays default to arrived: false (חופשה)
              // Weekends default to arrived: false (שישי/שבת)
              if (holiday) {
                finalResults.push({
                  id: carrier.id,
                  name: carrier.name,
                  date: dateStr,
                  arrived: false,
                  notes: `חופשה: ${holiday.name}`,
                  isPlaceholder: false,
                  isHolidayDay: true,
                  holidayName: holiday.name
                });
              } else if (isWeekendDay) {
                finalResults.push({
                  id: carrier.id,
                  name: carrier.name,
                  date: dateStr,
                  arrived: false,
                  notes: 'שישי/שבת - לא פעיל',
                  isPlaceholder: false,
                  isHolidayDay: false,
                  holidayName: ''
                });
              } else {
                finalResults.push({
                  id: carrier.id,
                  name: carrier.name,
                  date: dateStr,
                  arrived: true,
                  notes: '',
                  isPlaceholder: false,
                  isHolidayDay: false,
                  holidayName: ''
                });
              }
            }
          }
        }

        const sortedResults = finalResults.sort((a, b) => {
          const dateComp = (a.date || '').localeCompare(b.date || '');
          if (dateComp !== 0) return dateComp;
          return (a.name || '').localeCompare(b.name || '', 'he');
        });

        setData(sortedResults);
      } else if (reportType === 'staff') {
        const q = query(
          collection(db, 'staffAbsences'),
          where('startDate', '<=', effectiveEndDate)
        );
        const staffResults = await getCachedQuery(`staff_absences_${effectiveEndDate}`, q);
        let results = staffResults
          .filter((absence: any) => absence.endDate >= effectiveStartDate);
        
        if (staffReportType !== 'all') {
          results = results.filter((abs: any) => abs.staffType === staffReportType);
        }

        if (selectedStaffId !== 'all') {
          results = results.filter((abs: any) => abs.staffId === selectedStaffId);
        }

        setData(results.sort((a: any, b: any) => (a.startDate || '').localeCompare(b.startDate || '')));
      } else if (reportType === 'individual') {
        if (!selectedPersonId) return;
        
        if (individualType === 'student') {
          const allStudents = await getCachedStudents(false, academicYearId);
          const student = allStudents.find(s => s.id === selectedPersonId) || currentPeople.find(p => p.id === selectedPersonId);
          const studentFullName = student?.fullName?.trim() || student?.name?.trim() || '';
          const sId = String(selectedPersonId).trim();

          // 1. Fetch range attendance (single-field index on date, no composite index required)
          const rangeQ = query(
            collection(db, 'studentAttendance'),
            where('date', '>=', effectiveStartDate),
            where('date', '<=', effectiveEndDate),
            orderBy('date', 'asc')
          );
          const rangeResults = await getCachedQuery(`attendance_${effectiveStartDate}_${effectiveEndDate}`, rangeQ, true);

          // 2. Also query direct student attendance by studentId (single-field index)
          const studentQ = query(
            collection(db, 'studentAttendance'),
            where('studentId', '==', selectedPersonId)
          );
          let directResults: any[] = [];
          try {
            directResults = await getCachedQuery(`student_att_${selectedPersonId}`, studentQ, true);
          } catch (e) {
            console.warn("Direct student query note:", e);
          }

          // Build a set of dates where attendance was recorded for the student's class
          const classDatesWithAttendance = new Set<string>();
          const targetClassId = student?.classId;
          const targetClassName = student?.className;

          (rangeResults || []).forEach((att: any) => {
            if (att.date) {
              if (targetClassId && att.classId === targetClassId) {
                classDatesWithAttendance.add(att.date);
              }
              if (targetClassName && att.className === targetClassName) {
                classDatesWithAttendance.add(att.date);
              }
            }
          });

          // Match records belonging to this student
          const attendanceMap = new Map<string, any>();

          const checkAndAddAtt = (att: any) => {
            if (!att || !att.date) return;
            const dStr = normalizeDateStr(att.date);
            if (!dStr) return;
            const attSId = String(att.studentId || '').trim();
            const attSName = String(att.studentName || '').trim();
            const idParts = typeof att.id === 'string' ? att.id.split('_') : [];
            const docIdMatch = idParts.includes(sId);

            const isMatch = attSId === sId || 
              (studentFullName && attSName && (attSName === studentFullName || attSName.includes(studentFullName) || studentFullName.includes(attSName))) ||
              docIdMatch;

            if (isMatch) {
              attendanceMap.set(dStr, att);
            }
          };

          (rangeResults || []).forEach(checkAndAddAtt);
          (directResults || [])
            .filter((att: any) => att.date >= effectiveStartDate && att.date <= effectiveEndDate)
            .forEach(checkAndAddAtt);

          const finalResults = [];
          
          for (const dateStr of workdayDates) {
            const holiday = getHolidayForDate(dateStr, holidays);
            const isFutureDate = dateStr > todayStr;
            const att = attendanceMap.get(dateStr);

            if (holiday) {
              finalResults.push({
                studentId: sId,
                studentName: studentFullName,
                classId: student?.classId || '',
                className: student?.className || '',
                date: dateStr,
                present: false,
                notes: `חופשה: ${holiday.name}`,
                isPlaceholder: false,
                isHolidayDay: true,
                holidayName: holiday.name
              });
            } else if (att) {
              const isPresent = att.present === true || att.present === 'true' || att.status === 'present';
              finalResults.push({
                ...att,
                studentId: sId,
                studentName: studentFullName || att.studentName,
                classId: student?.classId || att.classId,
                className: student?.className || att.className,
                date: dateStr,
                present: isPresent,
                notes: att.notes || '',
                isPlaceholder: false,
                isHolidayDay: false,
                holidayName: ''
              });
            } else if (!isFutureDate) {
              // Past or current school day: in this system, students are present by default unless recorded absent
              finalResults.push({
                studentId: sId,
                studentName: studentFullName,
                classId: student?.classId || '',
                className: student?.className || '',
                date: dateStr,
                present: true,
                notes: '',
                isPlaceholder: false,
                isHolidayDay: false,
                holidayName: ''
              });
            } else {
              // Future date that has not yet occurred
              finalResults.push({
                studentId: sId,
                studentName: studentFullName,
                classId: student?.classId || '',
                className: student?.className || '',
                date: dateStr,
                present: false,
                notes: '(טרם דווח)',
                isPlaceholder: true,
                isHolidayDay: false,
                holidayName: ''
              });
            }
          }
          setData(finalResults.sort((a: any, b: any) => (a.date || '').localeCompare(b.date || '')));
        } else {
          const q = query(
            collection(db, 'staffAbsences'),
            where('staffId', '==', selectedPersonId),
            where('startDate', '<=', effectiveEndDate)
          );
          const staffResults = await getCachedQuery(`staff_indiv_${selectedPersonId}_${effectiveEndDate}`, q);
          const results = staffResults
            .filter((abs: any) => abs.endDate >= effectiveStartDate)
            .sort((a: any, b: any) => (a.startDate || '').localeCompare(b.startDate || ''));
          setData(results);
        }
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, reportType === 'attendance' ? 'studentAttendance' : reportType === 'transport' ? 'transportAttendance' : 'staffAbsences');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    if (data.length === 0) return;

    let exportData: any[] = [];
    let fileName = `report-${reportType}-${date}.xlsx`;

    if (reportType === 'attendance') {
      exportData = data.map(item => ({
        'שם תלמיד': item.studentName,
        'כיתה': item.className,
        'נוכחות': item.isHolidayDay ? 'חופשה' : (item.isPlaceholder ? 'אין דיווח' : (item.present ? 'נוכח' : 'נעדר')),
        'הערות': item.notes || ''
      }));
    } else if (reportType === 'general') {
      exportData = data.map(item => ({
        'שם כיתה': item.className || '',
        'נוכחות': item.present ?? 0,
        'סה"כ': item.total ?? 0,
        'נעדרים': Array.isArray(item.absentNames) ? item.absentNames.join(', ') : (item.absentNames || '')
      }));
    } else if (reportType === 'transport') {
      exportData = data.map(item => ({
        ...(dateRangeType !== 'daily' ? { 'תאריך': item.date } : {}),
        'שם מסיע': item.name,
        'סטטוס': item.isHolidayDay ? 'חופשה' : (item.arrived ? 'נוכח' : 'לא נוכח'),
        'הערות': item.notes || ''
      }));
    } else if (reportType === 'staff') {
      if (staffDisplayMode === 'summary') {
        const summaryMap: Record<string, any> = {};
        data.forEach(item => {
          const sid = item.staffId || item.staffName;
          if (!summaryMap[sid]) {
            summaryMap[sid] = {
              name: item.staffName,
              type: item.staffType === 'educational' ? 'חינוכי' : 'סייעת',
              totalDays: 0
            };
          }
          summaryMap[sid].totalDays += calculateDaysInRange(item.startDate, item.endDate, startDate, endDate);
        });
        exportData = Object.values(summaryMap).map(item => ({
          'שם עובד': item.name,
          'סוג': item.type,
          'סך ימי היעדרות': item.totalDays
        }));
      } else {
        exportData = data.map(item => ({
          'שם עובד': item.staffName,
          'סוג': item.staffType === 'educational' ? 'חינוכי' : 'סייעת',
          'מתאריך': item.startDate,
          'עד תאריך': item.endDate,
          'סה"כ ימי היעדרות': calculateDaysInRange(item.startDate, item.endDate, startDate, endDate),
          'סיבה': item.reason,
          'ממלא מקום': item.substitute || ''
        }));
      }
    } else if (reportType === 'individual') {
      if (individualType === 'student') {
        exportData = data.map(item => ({
          'תאריך': item.date,
          'נוכחות': item.isHolidayDay ? 'חופשה' : (item.isPlaceholder ? 'אין דיווח' : (item.present ? 'נוכח' : 'נעדר')),
          'הערות': item.notes || ''
        }));
      } else {
        exportData = data.map(item => ({
          'תאריך התחלה': item.startDate,
          'תאריך סיום': item.endDate,
          'סה"כ ימי היעדרות': calculateDaysInRange(item.startDate, item.endDate, startDate, endDate),
          'סיבה': item.reason,
          'אישור מחלה': item.hasCertificate ? 'כן' : 'לא'
        }));
      }
      fileName = `report-individual-${selectedPersonId}-${startDate}-${endDate}.xlsx`;
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, fileName);
  };

  const renderCarrierMonthlyPageHtml = (
    carrierName: string, 
    carrierData: any[], 
    monthName: string, 
    dateRangeStr: string,
    year?: number
  ) => {
    const sortedData = [...carrierData].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    
    const getHebrewDayOfWeek = (dateStr: string) => {
      try {
        const d = new Date(dateStr + 'T12:00:00');
        const days = ['יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי', 'יום שישי', 'שבת'];
        return days[d.getDay()] || '';
      } catch {
        return '';
      }
    };

    const isWeekendDay = (dateStr: string) => {
      try {
        const d = new Date(dateStr + 'T12:00:00');
        return d.getDay() === 5 || d.getDay() === 6;
      } catch {
        return false;
      }
    };

    const formatToDDMMYYYY = (dateStr: string): string => {
      if (!dateStr) return '';
      const parts = dateStr.trim().split('-');
      if (parts.length === 3) {
        const [y, m, d] = parts;
        return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
      }
      return dateStr;
    };

    const formatRangeInDDMMYYYY = (range: string): string => {
      if (!range) return '';
      return range.replace(/(\d{4})-(\d{2})-(\d{2})/g, (_match, y, m, d) => `${d}/${m}/${y}`);
    };

    const totalDays = sortedData.length;
    const arrivedCount = sortedData.filter(d => d.arrived).length;
    const holidayCount = sortedData.filter(d => d.isHolidayDay).length;
    const weekendCount = sortedData.filter(d => !d.isHolidayDay && (isWeekendDay(d.date) || (d.notes && d.notes.includes('שישי')))).length;
    const nonArrivalCount = sortedData.filter(d => !d.arrived && !d.isHolidayDay && !isWeekendDay(d.date) && !(d.notes && d.notes.includes('שישי'))).length;

    // Dynamically calculate row height and padding so the table fills the whole A4 page beautifully
    const totalRows = Math.max(totalDays, 1);
    const calculatedRowHeight = Math.floor(620 / totalRows);
    const rowHeight = Math.min(Math.max(calculatedRowHeight, 22), 34);
    const cellPaddingY = Math.max(Math.floor((rowHeight - 16) / 2), 3);

    const getStatusBadge = (item: any) => {
      if (item.isHolidayDay) {
        return `<span style="font-weight: 700; font-size: 11px; color: #92400e; white-space: nowrap;">חופשה</span>`;
      }
      if (isWeekendDay(item.date) || (item.notes && item.notes.includes('שישי'))) {
        return `<span style="font-weight: 600; font-size: 11px; color: #64748b; white-space: nowrap;">סופ״ש</span>`;
      }
      if (item.arrived) {
        return `<span style="font-weight: 900; font-size: 12.5px; color: #0f172a; white-space: nowrap;">✓ נוכח</span>`;
      }
      return `<span style="font-weight: 700; font-size: 11px; color: #991b1b; white-space: nowrap;">לא בוצע</span>`;
    };

    const renderRows = (items: any[]) => items.map((item, idx) => `
      <tr style="background-color: ${idx % 2 === 0 ? '#ffffff' : '#f8fafc'}; height: ${rowHeight}px;">
        <td style="padding: ${cellPaddingY}px 6px; border: 1px solid #cbd5e1; text-align: center; font-size: 11px; color: #64748b; font-weight: 700;">${idx + 1}</td>
        <td style="padding: ${cellPaddingY}px 10px; border: 1px solid #cbd5e1; font-weight: 800; font-size: 12px; color: #0f172a; white-space: nowrap;">${formatToDDMMYYYY(item.date)}</td>
        <td style="padding: ${cellPaddingY}px 10px; border: 1px solid #cbd5e1; font-size: 11.5px; color: #334155; font-weight: 600; white-space: nowrap;">${getHebrewDayOfWeek(item.date)}</td>
        <td style="padding: ${cellPaddingY}px 6px; border: 1px solid #cbd5e1; text-align: center;">
          ${getStatusBadge(item)}
        </td>
        <td style="padding: ${cellPaddingY}px 10px; border: 1px solid #cbd5e1; font-size: 11px; color: #475569; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          ${item.notes || '-'}
        </td>
      </tr>
    `).join('');

    const formattedDateRangeStr = formatRangeInDDMMYYYY(dateRangeStr);

    return `
      <div style="font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Noto Sans Hebrew', sans-serif; direction: rtl; color: #0f172a; box-sizing: border-box; background-color: #ffffff; height: 100%; display: flex; flex-direction: column; justify-content: space-between;">
        <div>
          <!-- Top decorative border accent -->
          <div style="height: 4px; background: linear-gradient(90deg, #312e81, #4f46e5, #6366f1); border-radius: 4px; margin-bottom: 12px;"></div>

          <!-- Official Header -->
          <div style="border-bottom: 1.5px solid #e2e8f0; padding-bottom: 10px; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <img src="${SCHOOL_LOGO_BASE64}" style="height: 52px; width: auto; object-fit: contain;" />
              <div style="text-align: right;">
                <h1 style="font-size: 19px; font-weight: 900; margin: 0; color: #0f172a; line-height: 1.25;">בית ספר אלמג'ד - אשכול גנים</h1>
                <p style="font-size: 13.5px; color: #312e81; margin: 3px 0 0 0; font-weight: 800;">
                  אישור ביצוע שירותי הסעות • מסיע: <span style="color: #0f172a; font-weight: 900; font-size: 14.5px;">${carrierName}</span>
                </p>
              </div>
            </div>
            <div style="text-align: left; display: flex; flex-direction: column; align-items: flex-end;">
              <div style="background-color: #f8fafc; border: 2px solid #312e81; color: #1e1b4b; font-size: 14.5px; font-weight: 900; padding: 5px 16px; border-radius: 8px; display: inline-block; box-shadow: 0 1px 3px rgba(0,0,0,0.06); letter-spacing: 0.2px;">
                חודש: ${monthName} ${year || ''}
              </div>
              <div style="font-size: 10.5px; color: #475569; margin-top: 4px; font-weight: 700;">
                תאריך הפקה: ${new Date().toLocaleDateString('he-IL')}
              </div>
            </div>
          </div>

          <!-- Summary Cards Grid (3 cards: Carrier, Range, Total Days Present - Clean styling, no green bg, no percentage) -->
          <div style="margin-bottom: 12px; display: grid; grid-template-columns: 1.4fr 1.3fr 1fr; gap: 8px;">
            <div style="border: 1.5px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; background-color: #f8fafc;">
              <div style="font-size: 10.5px; color: #64748b; font-weight: 700; margin-bottom: 3px;">שם מסיע:</div>
              <div style="font-size: 14.5px; font-weight: 900; color: #0f172a; word-break: break-word; line-height: 1.25;">${carrierName}</div>
            </div>
            <div style="border: 1.5px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; background-color: #f8fafc;">
              <div style="font-size: 10.5px; color: #64748b; font-weight: 700; margin-bottom: 3px;">טווח תאריכים:</div>
              <div style="font-size: 13px; font-weight: 800; color: #334155; margin-top: 2px;">${formattedDateRangeStr}</div>
            </div>
            <div style="border: 1.5px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; background-color: #f8fafc;">
              <div style="font-size: 10.5px; color: #64748b; font-weight: 700; margin-bottom: 3px;">סה"כ ימי נוכחות:</div>
              <div style="font-size: 15px; font-weight: 900; color: #0f172a; margin-top: 2px;">${arrivedCount} ימים</div>
            </div>
          </div>

          <!-- Section Title & Meta -->
          <div style="margin-bottom: 6px; display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 11.5px; font-weight: 800; color: #1e293b;">יומן ביצוע ונוכחות יומי מפורט:</span>
            <span style="font-size: 10px; color: #64748b; font-weight: 600;">(סה"כ ימים: ${totalDays} • חופשות: ${holidayCount} • סופ״ש: ${weekendCount}${nonArrivalCount > 0 ? ` • לא בוצע: ${nonArrivalCount}` : ''})</span>
          </div>

          <!-- Single Unified Table for All Dates -->
          <table style="width: 100%; border-collapse: collapse; border: 1.5px solid #cbd5e1; text-align: right; margin-bottom: 8px;">
            <thead>
              <tr style="background-color: #1e1b4b; color: #ffffff; font-size: 11px; height: 32px;">
                <th style="padding: 4px 6px; border: 1px solid #312e81; text-align: center; width: 38px;">#</th>
                <th style="padding: 4px 10px; border: 1px solid #312e81; width: 115px;">תאריך</th>
                <th style="padding: 4px 10px; border: 1px solid #312e81; width: 105px;">יום בשבוע</th>
                <th style="padding: 4px 6px; border: 1px solid #312e81; text-align: center; width: 125px;">סטטוס ביצוע</th>
                <th style="padding: 4px 10px; border: 1px solid #312e81;">הערות</th>
              </tr>
            </thead>
            <tbody>
              ${renderRows(sortedData)}
            </tbody>
          </table>
        </div>

        <div>
          <!-- Legal Declaration Box -->
          <div style="border: 1px solid #e2e8f0; background-color: #f8fafc; padding: 7px 14px; border-radius: 8px; font-size: 10px; color: #334155; margin-bottom: 12px; text-align: center; line-height: 1.4;">
            הריני לאשר בזאת כי שירותי ההסעה עבור המסיע/הקו הנ״ל בוצעו כסדרם בימי הלימודים הפעילים המפורטים לעיל לשנת הלימודים.
          </div>

          <!-- Signatures Area -->
          <div style="display: flex; justify-content: space-around; padding: 0 40px; margin-bottom: 12px;">
            <div style="text-align: center; width: 230px;">
              <div style="font-size: 11.5px; font-weight: 800; color: #1e293b; margin-bottom: 4px;">חתימת המסיע / נציג ההסעות</div>
              <div style="border-bottom: 1.5px dashed #475569; height: 42px; margin-bottom: 5px;"></div>
              <div style="font-size: 10px; color: #64748b; font-weight: 600;">שם: ${carrierName}</div>
            </div>
            <div style="text-align: center; width: 230px;">
              <div style="font-size: 11.5px; font-weight: 800; color: #1e293b; margin-bottom: 4px;">אישור וחותמת הנהלת בית הספר</div>
              <div style="border-bottom: 1.5px dashed #475569; height: 42px; margin-bottom: 5px;"></div>
              <div style="font-size: 10px; color: #64748b; font-weight: 600;">תאריך אישור: ${new Date().toLocaleDateString('he-IL')}</div>
            </div>
          </div>

          <!-- Official Footer -->
          <div style="border-top: 1px solid #e2e8f0; padding-top: 5px; text-align: center; font-size: 8.5px; color: #94a3b8; font-weight: 500;">
            מערכת ניהול אלמג'ד • אישור רשמי לדיווח חודשי • הופק ב-${new Date().toLocaleDateString('he-IL')} • אסמכתא ממוחשבת תקפה • עמוד 1 מתוך 1
          </div>
        </div>
      </div>
    `;
  };

  const exportPDF = async () => {
    setLoading(true);
    
    try {
      // Special handling for transport report (always guaranteed exactly 1 page per carrier!)
      if (reportType === 'transport' && data.length > 0) {
        const carriersToExport = selectedCarrierId === 'all'
          ? Array.from(new Set(data.map(d => d.id || d.name).filter(Boolean)))
          : [selectedCarrierId];

        const targetDateStr = dateRangeType === 'daily' ? date : startDate;
        const monthName = getMonthName(targetDateStr);
        const year = getReportYear(targetDateStr);
        const dateRangeStr = dateRangeType === 'daily' ? date : `${startDate} עד ${endDate}`;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        for (let i = 0; i < carriersToExport.length; i++) {
          const carrierId = carriersToExport[i];
          const found = data.find(d => d.id === carrierId || d.name === carrierId) || carriers.find(c => c.id === carrierId || c.name === carrierId);
          const carrierName = found?.name || carrierId || 'מסיע';
          const carrierData = data.filter(d => (d.id && d.id === carrierId) || (d.name && d.name === carrierName));

          if (i > 0) pdf.addPage();

          const tempDiv = document.createElement('div');
          tempDiv.style.width = '794px'; 
          tempDiv.style.height = '1120px';
          tempDiv.style.minHeight = '1120px';
          tempDiv.style.padding = '24px 28px 20px 28px';
          tempDiv.style.boxSizing = 'border-box';
          tempDiv.style.backgroundColor = '#ffffff';
          tempDiv.style.direction = 'rtl';
          tempDiv.style.fontFamily = 'system-ui, -apple-system, sans-serif';
          tempDiv.style.position = 'fixed';
          tempDiv.style.left = '-9999px';
          tempDiv.style.top = '0';

          tempDiv.innerHTML = renderCarrierMonthlyPageHtml(
            carrierName, 
            carrierData, 
            monthName, 
            dateRangeStr,
            year
          );

          document.body.appendChild(tempDiv);
          const canvas = await safeHtml2Canvas(tempDiv, { scale: 2 });
          document.body.removeChild(tempDiv);

          const imgData = canvas.toDataURL('image/png');
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          const marginX = 8;
          const marginY = 8;
          const maxWidth = pdfWidth - (marginX * 2);
          const maxHeight = pageHeight - (marginY * 2);

          let renderWidth = maxWidth;
          let renderHeight = (imgProps.height * renderWidth) / imgProps.width;

          // Guaranteed exact 1-page fit
          if (renderHeight > maxHeight) {
            const scale = maxHeight / renderHeight;
            renderHeight = maxHeight;
            renderWidth = renderWidth * scale;
          }

          const xOffset = marginX + ((maxWidth - renderWidth) / 2);
          const yOffset = marginY + ((maxHeight - renderHeight) / 2);

          pdf.addImage(imgData, 'PNG', xOffset, yOffset, renderWidth, renderHeight);
        }

        const fileName = carriersToExport.length === 1
          ? `אישור_הסעות_${data.find(d => d.id === carriersToExport[0])?.name || 'מסיע'}_${monthName}.pdf`
          : `אישורי_הסעות_מרוכז_${monthName}.pdf`;

        pdf.save(fileName);
        return;
      }

      if (!reportRef.current) return;
      const canvas = await safeHtml2Canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        stripStylesheets: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginX = 8;
      const marginY = 8;
      const maxWidth = pdfWidth - (marginX * 2);
      const maxHeight = pageHeight - (marginY * 2);

      const renderWidth = maxWidth;
      const pdfHeight = (imgProps.height * renderWidth) / imgProps.width;
      
      if (pdfHeight > maxHeight) {
        let heightLeft = pdfHeight;
        let position = marginY;
        
        pdf.addImage(imgData, 'PNG', marginX, position, renderWidth, pdfHeight);
        heightLeft -= maxHeight;
        
        while (heightLeft > 0) {
          position = marginY - (pdfHeight - heightLeft);
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', marginX, position, renderWidth, pdfHeight);
          heightLeft -= maxHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', marginX, marginY, renderWidth, pdfHeight);
      }
      
      pdf.save(`report-${reportType}-${date}.pdf`);
    } catch (error) {
      console.error("PDF Export Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportIndividualCarrierPDFs = async () => {
    setLoading(true);
    try {
      const carriersToExport = selectedCarrierId === 'all' 
        ? Array.from(new Set(data.map(d => d.id || d.name).filter(Boolean)))
        : [selectedCarrierId];

      const targetDateStr = dateRangeType === 'daily' ? date : startDate;
      const monthName = getMonthName(targetDateStr);
      const year = getReportYear(targetDateStr);
      const dateRangeStr = dateRangeType === 'daily' ? date : `${startDate} עד ${endDate}`;

      for (const carrierId of carriersToExport) {
        const found = data.find(d => d.id === carrierId || d.name === carrierId) || carriers.find(c => c.id === carrierId || c.name === carrierId);
        const carrierName = found?.name || carrierId || 'מסיע';
        const carrierData = data.filter(d => (d.id && d.id === carrierId) || (d.name && d.name === carrierName));
        
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Create a temporary container for this carrier
        const tempDiv = document.createElement('div');
        tempDiv.style.width = '794px'; 
        tempDiv.style.height = '1120px';
        tempDiv.style.minHeight = '1120px';
        tempDiv.style.padding = '24px 28px 20px 28px';
        tempDiv.style.boxSizing = 'border-box';
        tempDiv.style.backgroundColor = '#ffffff';
        tempDiv.style.direction = 'rtl';
        tempDiv.style.fontFamily = 'system-ui, -apple-system, sans-serif';
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '0';
        
        tempDiv.innerHTML = renderCarrierMonthlyPageHtml(
          carrierName, 
          carrierData, 
          monthName, 
          dateRangeStr,
          year
        );
        
        document.body.appendChild(tempDiv);
        const canvas = await safeHtml2Canvas(tempDiv, { scale: 2 });
        document.body.removeChild(tempDiv);
        
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        const marginX = 8;
        const marginY = 8;
        const maxWidth = pdfWidth - (marginX * 2);
        const maxHeight = pageHeight - (marginY * 2);

        let renderWidth = maxWidth;
        let renderHeight = (imgProps.height * renderWidth) / imgProps.width;

        // Guaranteed exact 1-page fit
        if (renderHeight > maxHeight) {
          const scale = maxHeight / renderHeight;
          renderHeight = maxHeight;
          renderWidth = renderWidth * scale;
        }

        const xOffset = marginX + ((maxWidth - renderWidth) / 2);
        const yOffset = marginY + ((maxHeight - renderHeight) / 2);

        pdf.addImage(imgData, 'PNG', xOffset, yOffset, renderWidth, renderHeight);
        pdf.save(`אישור_הסעות_${carrierName}_${monthName}.pdf`);
      }
    } catch (error) {
      console.error("Error generating individual PDFs:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportMail = () => {
    if (data.length === 0) return;
    const dateStr = dateRangeType === 'daily' ? date : `${startDate} - ${endDate}`;
    const subject = `דוח ${reportType} - ${dateStr}`;
    const body = `שלום,\n\nמצורף סיכום דוח ${reportType}.\nטווח תאריכים: ${dateStr}\nמספר רשומות: ${data.length}\n\nבברכה,\nצוות בית הספר`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSetAllCampDays = async () => {
    try {
      const holidays = await getHolidays();
      if (holidays.length > 0) {
        const startDates = holidays.map(h => h.startDate).sort();
        const endDates = holidays.map(h => h.endDate).sort();
        setStartDate(startDates[0]);
        setEndDate(endDates[endDates.length - 1]);
        setDateRangeType('all');
      }
    } catch (error) {
      console.error("Error setting all camp days:", error);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <FileText size={28} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-slate-900">הפקת דוחות</h2>
                <span className="text-xs font-bold px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-100">
                  {currentYearObj?.name || academicYearId}
                </span>
              </div>
              <p className="text-sm text-slate-500">צפה וייצא דוחות נוכחות יומיים</p>
            </div>
          </div>
          
          <div className="bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
            <img 
              src="/logo.png" 
              alt="School Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
        
        <div className="flex bg-slate-100 p-1.5 rounded-2xl self-start md:self-center overflow-x-auto max-w-full mb-8">
            <button
              onClick={() => { setData([]); setLoading(true); setReportType('attendance'); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all text-sm font-bold whitespace-nowrap ${
                reportType === 'attendance' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Users size={18} />
              נוכחות תלמידים
            </button>
            <button
              onClick={() => { setData([]); setLoading(true); setReportType('general'); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all text-sm font-bold whitespace-nowrap ${
                reportType === 'general' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <FileText size={18} />
              דוח נוכחות כללי
            </button>
            <button
              onClick={() => { setData([]); setLoading(true); setReportType('transport'); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all text-sm font-bold whitespace-nowrap ${
                reportType === 'transport' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Truck size={18} />
              נוכחות מסיעים
            </button>
            <button
              onClick={() => { setData([]); setLoading(true); setReportType('staff'); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all text-sm font-bold whitespace-nowrap ${
                reportType === 'staff' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <UserX size={18} />
              היעדרויות צוות
            </button>
            <button
              onClick={() => { setData([]); setLoading(true); setReportType('individual'); }}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all text-sm font-bold whitespace-nowrap ${
                reportType === 'individual' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <CalendarIcon size={18} />
              דוח אישי
            </button>
          </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Time and Export Settings */}
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                <CalendarIcon size={16} className="text-indigo-500" />
                טווח זמן
              </label>
              <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-200">
                <button
                  onClick={() => setDateRangeType('daily')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    dateRangeType === 'daily' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  יומי
                </button>
                <button
                  onClick={() => setDateRangeType('month')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    dateRangeType === 'month' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  חודשי
                </button>
                <button
                  onClick={() => setDateRangeType('custom')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    dateRangeType === 'custom' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  טווח
                </button>
                <button
                  onClick={handleSetAllCampDays}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    dateRangeType === 'all' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  כל הקייטנה
                </button>
              </div>
            </div>

            {dateRangeType === 'daily' ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <CalendarIcon size={16} className="text-indigo-500" />
                  תאריך
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
            ) : dateRangeType === 'month' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">חודש</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {[
                          'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                          'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
                        ][i]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">שנה</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <CalendarIcon size={16} className="text-indigo-500" />
                    מתאריך
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <CalendarIcon size={16} className="text-indigo-500" />
                    עד תאריך
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  />
                </div>
              </div>
            )}

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <label className="block text-sm font-bold text-slate-700 mb-3">סינון נתונים להצגה וייצוא:</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setExportFilter('all')}
                  className={`flex-1 py-2.5 rounded-xl border-2 transition-all font-bold text-sm ${
                    exportFilter === 'all' ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-slate-100 bg-white text-slate-500 hover:border-indigo-200'
                  }`}
                >
                  הצג הכל
                </button>
                <button
                  onClick={() => setExportFilter('present')}
                  className={`flex-1 py-2.5 rounded-xl border-2 transition-all font-bold text-sm ${
                    exportFilter === 'present' ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-slate-100 bg-white text-slate-500 hover:border-indigo-200'
                  }`}
                >
                  נוכחים בלבד
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Report Filters and Search */}
          <div className="space-y-6">
            {reportType === 'transport' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <Truck size={16} className="text-indigo-500" />
                  בחירת מסיע
                </label>
                <select
                  value={selectedCarrierId}
                  onChange={(e) => setSelectedCarrierId(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                >
                  <option value="all">כל המסיעים</option>
                  {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {reportType === 'staff' && (
              <div className="space-y-4">
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                      <LayoutList size={16} className="text-indigo-500" />
                      סוג דוח
                    </label>
                    <div className="flex bg-slate-100 p-1 rounded-xl">
                      <button
                        onClick={() => setStaffDisplayMode('detailed')}
                        className={`flex-1 py-2 px-3 text-xs font-bold rounded-lg transition-all ${staffDisplayMode === 'detailed' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        מפורט
                      </button>
                      <button
                        onClick={() => setStaffDisplayMode('summary')}
                        className={`flex-1 py-2 px-3 text-xs font-bold rounded-lg transition-all ${staffDisplayMode === 'summary' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        ריכוזי (סיכום ימים)
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                      <UserX size={16} className="text-indigo-500" />
                      סוג צוות
                    </label>
                  <select
                    value={staffReportType}
                    onChange={(e) => {
                      setStaffReportType(e.target.value as any);
                      setSelectedStaffId('all');
                    }}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    <option value="all">כל הצוות</option>
                    <option value="educational">צוות חינוכי/טיפולי</option>
                    <option value="assistant">מסייעות</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <Users size={16} className="text-indigo-500" />
                    סינון לפי איש צוות
                  </label>
                  <select
                    value={selectedStaffId}
                    onChange={(e) => setSelectedStaffId(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    <option value="all">כל העובדים</option>
                    {people
                      .filter(p => staffReportType === 'all' || (p as any).staffType === staffReportType)
                      .map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                  </select>
                </div>
              </div>
            )}

            {reportType === 'individual' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <Filter size={16} className="text-indigo-500" />
                  סוג דוח אישי
                </label>
                <select
                  value={individualType}
                  onChange={(e) => { setIndividualType(e.target.value as any); setSelectedPersonId(''); }}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                >
                  <option value="student">תלמיד</option>
                  <option value="staff">איש צוות</option>
                </select>
              </div>
            )}

            {(reportType === 'attendance' || (reportType === 'individual' && individualType === 'student')) && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <Filter size={16} className="text-indigo-500" />
                    סינון לפי כיתה
                  </label>
                  <select
                    value={selectedClassId}
                    onChange={(e) => {
                      setSelectedClassId(e.target.value);
                      setSelectedStudentId('all');
                    }}
                    disabled={userRole === 'teacher' && !!assignedClassId}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:opacity-60"
                  >
                    {userRole !== 'teacher' && <option value="all">כל הכיתות</option>}
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                {reportType === 'attendance' && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                      <Users size={16} className="text-indigo-500" />
                      סינון לפי תלמיד
                    </label>
                    <select
                      value={selectedStudentId}
                      onChange={(e) => setSelectedStudentId(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    >
                      <option value="all">כל התלמידים</option>
                      {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {reportType === 'individual' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <Users size={16} className="text-indigo-500" />
                  בחירת {individualType === 'student' ? 'תלמיד' : 'איש צוות'}
                </label>
                <select
                  value={selectedPersonId}
                  onChange={(e) => setSelectedPersonId(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                >
                  <option value="">בחר...</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            <div className="pt-4">
              <button
                onClick={fetchReport}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-2xl transition-all shadow-xl shadow-indigo-100 disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : <Search size={24} />}
                <span className="text-lg">הצגת דוח</span>
              </button>
            </div>
          </div>
        </div>

        {isWeekend && dateRangeType === 'daily' && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 text-amber-700">
            <AlertTriangle size={20} />
            <p className="text-sm font-bold">שים לב: התאריך הנבחר הוא יום שישי או שבת. בית הספר אינו עובד בימים אלו.</p>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div 
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-20 flex flex-col items-center justify-center gap-4"
          >
            <Loader2 className="animate-spin text-indigo-600" size={48} />
            <p className="text-slate-500 font-medium">מפיק דוח...</p>
          </motion.div>
        ) : data.length > 0 ? (
          <motion.div 
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
          >
            <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-pulse"></div>
                <span className="font-bold text-slate-700">תוצאות הדוח ({data.length} רשומות)</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {reportType === 'transport' && (
                  <button
                    onClick={exportIndividualCarrierPDFs}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl transition-all font-bold shadow-lg shadow-indigo-100"
                    title={selectedCarrierId === 'all' ? 'מפיק קובץ PDF נפרד לכל מסיע (עמוד 1 לכל מסיע)' : 'מפיק אישור חודשי רשמי מותאם בדיוק לעמוד 1'}
                  >
                    <FileDown size={18} />
                    <span>{selectedCarrierId === 'all' ? 'הפק אישורים נפרדים (PDF)' : 'אישור חודשי רשמי (עמוד 1)'}</span>
                  </button>
                )}
                <button
                  onClick={exportPDF}
                  className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl transition-all font-bold shadow-lg shadow-slate-100"
                  title={reportType === 'transport' ? 'הורדת אישור הסעות מותאם בדיוק לעמוד 1' : 'הורדת דוח בפורמט PDF'}
                >
                  <Download size={18} />
                  <span>{reportType === 'transport' ? 'אישור הסעות (PDF עמוד 1)' : 'PDF'}</span>
                </button>
                <button
                  onClick={exportExcel}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl transition-all font-bold shadow-lg shadow-emerald-100"
                >
                  <FileDown size={18} />
                  <span>Excel</span>
                </button>
              </div>
            </div>
            <div ref={reportRef} className="p-8 bg-white pdf-report-container" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
              {/* Report Header */}
              <div className="flex items-center justify-between mb-8 border-b pb-6" style={{ borderColor: '#e2e8f0' }}>
                <div className="flex items-center gap-4">
                  <img src="/logo.png" alt="Logo" className="h-20 w-auto object-contain" />
                  <div>
                    <h1 className="text-2xl font-bold" style={{ color: '#0f172a' }}>בית ספר אלמג'ד - אשכול גנים</h1>
                    <p style={{ color: '#64748b' }}>
                      {reportType === 'transport' 
                        ? `אישור ביצוע שירותי הסעות לחודש ${getMonthName()} ${getReportYear()}`
                        : 'דוח נוכחות והיעדרויות'}
                    </p>
                  </div>
                </div>
                <div className="text-left">
                  <div className="inline-block bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1 rounded-lg text-xs font-bold mb-1">
                    חודש: {getMonthName()} {getReportYear()}
                  </div>
                  <p className="font-bold" style={{ color: '#0f172a' }}>{new Date().toLocaleDateString('he-IL')}</p>
                  <p className="text-sm" style={{ color: '#64748b' }}>{dateRangeType === 'month' ? `חודש: ${getMonthName()} ${getReportYear()}` : dateRangeType === 'daily' ? `תאריך: ${date}` : `טווח: ${startDate} - ${endDate}`}</p>
                  {reportType === 'transport' && (
                    <p className="text-sm font-bold mt-1" style={{ color: '#0f172a' }}>
                      מסיע: {selectedCarrierId === 'all' ? 'כל המסיעים' : carriers.find(c => c.id === selectedCarrierId)?.name || selectedCarrierId}
                    </p>
                  )}
                </div>
              </div>

              <h2 className="text-xl font-bold mb-6 text-center" style={{ color: '#1e293b' }}>
                {reportType === 'attendance' ? (
                  selectedStudentId !== 'all' 
                    ? `דוח נוכחות לתלמיד: ${people.find(p => p.id === selectedStudentId)?.name || ''}`
                    : `דוח נוכחות כיתה ${selectedClassId === 'all' ? 'כל הכיתות' : classes.find(c => c.id === selectedClassId)?.name || ''}`
                ) :
                 reportType === 'general' ? 'דוח נוכחות כללי - ריכוז כיתות' :
                 reportType === 'transport' ? (
                   selectedCarrierId !== 'all' 
                     ? `דוח נוכחות מסיע: ${carriers.find(c => c.id === selectedCarrierId)?.name || ''}`
                     : 'דוח נוכחות מסיעים'
                 ) :
                 reportType === 'staff' ? (
                   selectedStaffId !== 'all'
                     ? `דוח היעדרויות אישי: ${people.find(p => p.id === selectedStaffId)?.name || ''}`
                     : (staffDisplayMode === 'summary' ? 'דו"ח ריכוז ימי היעדרות (סיכום)' :
                        staffReportType === 'educational' ? 'דוח היעדרויות צוות חינוכי/טיפולי' :
                        staffReportType === 'assistant' ? 'דוח היעדרויות צוות מסייעות' :
                        'דוח היעדרויות צוות כללי')
                 ) :
                 `דוח אישי - ${people.find(p => p.id === selectedPersonId)?.name || ''}`}
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full text-right border-collapse border" style={{ borderColor: '#e2e8f0' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}>
                      {reportType === 'attendance' ? (
                        <>
                          {dateRangeType !== 'daily' && <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>תאריך</th>}
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>שם תלמיד</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>כיתה</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>נוכחות</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>הערות</th>
                        </>
                      ) : reportType === 'general' ? (
                        <>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>שם כיתה</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>נוכחות/סה"כ</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>תלמידים שנעדרו</th>
                        </>
                      ) : reportType === 'transport' ? (
                        <>
                          {dateRangeType !== 'daily' && <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>תאריך</th>}
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>שם מסיע</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סטטוס (נוכח / לא נוכח)</th>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>הערות</th>
                        </>
                      ) : reportType === 'staff' ? (
                        staffDisplayMode === 'summary' ? (
                          <>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>שם עובד</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סוג צוות</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סך ימי היעדרות</th>
                          </>
                        ) : (
                          <>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>שם עובד</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>מתאריך</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>עד תאריך</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סה"כ ימים</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סיבה</th>
                            <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>ממלא מקום</th>
                          </>
                        )
                      ) : (
                        <>
                          <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>תאריך</th>
                          {individualType === 'student' ? (
                            <>
                              <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>נוכחות</th>
                              <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>הערות</th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>תאריך סיום</th>
                              <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סה"כ ימים</th>
                              <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>סיבה</th>
                              <th className="px-4 py-3 font-bold text-sm border" style={{ borderColor: '#6366f1' }}>אישור מחלה</th>
                            </>
                          )}
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: '#e2e8f0' }}>
                    {staffDisplayMode === 'summary' && reportType === 'staff' ? (
                      Object.values((data || []).reduce((acc: any, item: any) => {
                        if (!item) return acc;
                        const sid = item.staffId || item.staffName || 'unknown';
                        if (!acc[sid]) {
                          acc[sid] = {
                            staffName: item.staffName || 'לא ידוע',
                            staffType: item.staffType,
                            totalDays: 0
                          };
                        }
                        acc[sid].totalDays += calculateDaysInRange(item.startDate, item.endDate, startDate, endDate);
                        return acc;
                      }, {})).map((item: any, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.staffName}</td>
                          <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.staffType === 'educational' ? 'חינוכי' : 'סייעת'}</td>
                          <td className="px-4 py-3 text-sm border font-bold text-indigo-600" style={{ borderColor: '#e2e8f0' }}>{item.totalDays} ימים</td>
                        </tr>
                      ))
                    ) : (
                      data
                        .filter(item => {
                          if (exportFilter === 'all') return true;
                          if (reportType === 'attendance') return item.present;
                          if (reportType === 'transport') return item.arrived;
                          if (reportType === 'staff') return false; 
                          if (reportType === 'individual' && individualType === 'student') return item.present;
                          return true;
                        })
                        .map((item, idx) => {
                          let bgColor = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
                          let isPresent = false;
                          if (reportType === 'attendance') isPresent = item.present;
                          else if (reportType === 'transport') isPresent = item.arrived;
                          else if (reportType === 'individual' && individualType === 'student') isPresent = item.present;
                          
                          if (item.isHolidayDay) bgColor = '#fffbeb'; 
                          else if (item.isPlaceholder) bgColor = '#f8fafc'; 
                          else if (reportType !== 'transport' && isPresent) bgColor = '#f0fdf4'; 
                          else if (reportType !== 'staff' && reportType !== 'general' && reportType !== 'transport' && !isPresent) bgColor = '#fef2f2'; 

                          return (
                            <tr key={idx} style={{ backgroundColor: bgColor }}>
                              {reportType === 'attendance' ? (
                                <>
                                  {dateRangeType !== 'daily' && <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.date}</td>}
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.studentName}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.className}</td>
                                  <td className="px-4 py-3 text-sm border font-bold" style={{ borderColor: '#e2e8f0' }}>
                                    {item.isHolidayDay ? (
                                      <span className="text-amber-700">חופשה</span>
                                    ) : item.isPlaceholder ? (
                                      <span className="text-slate-400">אין דיווח</span>
                                    ) : item.present ? (
                                      <span className="text-emerald-700">נוכח</span>
                                    ) : (
                                      <span className="text-rose-700">נעדר</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.notes || '-'}</td>
                                </>
                              ) : reportType === 'general' ? (
                                <>
                                  <td className="px-4 py-3 text-sm border font-bold" style={{ borderColor: '#e2e8f0' }}>{item.className || '-'}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.present ?? 0} / {item.total ?? 0}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0', color: '#dc2626' }}>
                                    {Array.isArray(item.absentNames) ? (item.absentNames.join(', ') || '-') : (item.absentNames || '-')}
                                  </td>
                                </>
                              ) : reportType === 'transport' ? (
                                <>
                                  {dateRangeType !== 'daily' && (
                                    <td className="px-4 py-3 text-sm border font-semibold text-slate-800" style={{ borderColor: '#e2e8f0' }}>
                                      {item.date?.includes('-') ? item.date.split('-').reverse().join('/') : item.date}
                                    </td>
                                  )}
                                  <td className="px-4 py-3 text-sm border font-bold text-slate-900" style={{ borderColor: '#e2e8f0' }}>{item.name}</td>
                                  <td className="px-4 py-3 text-sm border font-bold text-center" style={{ borderColor: '#e2e8f0' }}>
                                    {item.isHolidayDay ? (
                                      <span className="text-amber-700 font-bold">חופשה</span>
                                    ) : item.arrived ? (
                                      <span className="font-bold text-slate-900 inline-flex items-center justify-center gap-1.5 text-sm">
                                        <span className="text-emerald-700 font-extrabold text-base">✓</span>
                                        <span>נוכח</span>
                                      </span>
                                    ) : (
                                      <span className="font-bold text-rose-700 text-sm">
                                        לא נוכח
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm border text-slate-600" style={{ borderColor: '#e2e8f0' }}>{item.notes || '-'}</td>
                                </>
                              ) : reportType === 'staff' ? (
                                <>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.staffName}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.startDate}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.endDate}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{calculateDaysInRange(item.startDate, item.endDate, startDate, endDate)}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.reason}</td>
                                  <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.substitute || '-'}</td>
                                </>
                              ) : (
                              <>
                                <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{individualType === 'student' ? item.date : item.startDate}</td>
                                {individualType === 'student' ? (
                                  <>
                                    <td className="px-4 py-3 text-sm border font-bold" style={{ borderColor: '#e2e8f0' }}>
                                      {item.isHolidayDay ? (
                                        <span className="text-amber-700">חופשה</span>
                                      ) : item.isPlaceholder ? (
                                        <span className="text-slate-400">אין דיווח</span>
                                      ) : item.present ? (
                                        <span className="text-emerald-700">נוכח</span>
                                      ) : (
                                        <span className="text-rose-700">נעדר</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.notes || '-'}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.endDate}</td>
                                    <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{calculateDaysInRange(item.startDate, item.endDate, startDate, endDate)}</td>
                                    <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.reason}</td>
                                    <td className="px-4 py-3 text-sm border" style={{ borderColor: '#e2e8f0' }}>{item.hasCertificate ? 'כן' : 'לא'}</td>
                                  </>
                                )}
                              </>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {(reportType === 'staff' || (reportType === 'individual' && individualType === 'staff')) && data.length > 0 && (
                    <tfoot className="bg-rose-50 font-bold">
                      <tr>
                        <td colSpan={reportType === 'staff' ? 3 : 2} className="px-4 py-3 text-sm border text-rose-900" style={{ borderColor: '#fecaca' }}>
                          סה"כ ימי היעדרות בתקופה:
                        </td>
                        <td className="px-4 py-3 text-sm border text-rose-700" style={{ borderColor: '#fecaca' }}>
                          {data.reduce((sum, item) => sum + calculateDaysInRange(item.startDate, item.endDate, startDate, endDate), 0)} ימים
                        </td>
                        <td colSpan={reportType === 'staff' ? 2 : 2} className="px-4 py-3 text-sm border" style={{ borderColor: '#fecaca' }}></td>
                      </tr>
                    </tfoot>
                  )}
                  {reportType === 'transport' && data.length > 0 && (
                    <tfoot className="bg-indigo-50 font-bold">
                      <tr>
                        <td colSpan={dateRangeType !== 'daily' ? 2 : 1} className="px-4 py-3 text-sm border text-indigo-900" style={{ borderColor: '#c7d2fe' }}>
                          סה"כ ימי עבודה (נוכחות):
                        </td>
                        <td className="px-4 py-3 text-sm border text-emerald-700" style={{ borderColor: '#c7d2fe' }}>
                          {data.filter(i => i.arrived).length} ימים
                        </td>
                        <td className="px-4 py-3 text-sm border" style={{ borderColor: '#c7d2fe' }}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="no-data"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white p-16 rounded-3xl shadow-sm border border-slate-100 text-center"
          >
            <div className="inline-flex p-5 bg-slate-50 text-slate-300 rounded-full mb-4">
              <Search size={40} />
            </div>
            <p className="text-slate-700 font-bold text-lg">לא נמצאו נתונים לתאריך ולפרמטרים שנבחרו</p>
            <p className="text-slate-400 text-sm mt-1">נסה לבחור תאריך או מסיע/כיתה אחרת, או לחץ על הכפתור מטה</p>
            <button
              onClick={fetchReport}
              className="mt-5 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-sm transition-all shadow-md shadow-indigo-100"
            >
              רענן דוח
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reports;
